import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import { ListItemSchema, type ChatPresenterComponent } from '@core/tellent-assistant-contracts';
import type { ListItem, PresentListArgs } from '@core/tellent-assistant-types';
import { HeadingComponent } from '@core/user-interface-legacy';

import { ListItemCandidateComponent } from './items/list-item-candidate/list-item-candidate.component';
import { ListItemEvaluationComponent } from './items/list-item-evaluation/list-item-evaluation.component';
import { ListItemEventComponent } from './items/list-item-event/list-item-event.component';
import { ListItemJobComponent } from './items/list-item-job/list-item-job.component';
import { ListItemLinkComponent } from './items/list-item-link/list-item-link.component';
import { ListItemRequisitionComponent } from './items/list-item-requisition/list-item-requisition.component';
import { ListItemTalentPoolComponent } from './items/list-item-talent-pool/list-item-talent-pool.component';
import { ListItemTaskComponent } from './items/list-item-task/list-item-task.component';
import { ListItemTeamMemberComponent } from './items/list-item-team-member/list-item-team-member.component';

const INITIAL_LIMIT = 8;

@Component({
  selector: 'rt-assistant-present-list',
  imports: [
    TranslatePipe,
    HeadingComponent,
    ListItemCandidateComponent,
    ListItemTeamMemberComponent,
    ListItemJobComponent,
    ListItemTalentPoolComponent,
    ListItemLinkComponent,
    ListItemEventComponent,
    ListItemEvaluationComponent,
    ListItemTaskComponent,
    ListItemRequisitionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './present-list.component.html',
  styleUrl: './present-list.component.less',
})
export class PresentListComponent implements ChatPresenterComponent<PresentListArgs> {
  protected readonly expanded = signal(false);

  protected readonly title = computed(() => this.args().title);
  protected readonly items = computed<readonly ListItem[]>(() => {
    const raw = this.args()?.items;
    if (!Array.isArray(raw)) return [];
    const valid: ListItem[] = [];
    for (const item of raw) {
      const parsed = ListItemSchema.safeParse(item);
      if (parsed.success) valid.push(parsed.data);
    }
    return valid;
  });

  protected readonly rendered = computed<readonly ListItem[]>(() => {
    const all = this.items();
    return this.expanded() ? all : all.slice(0, INITIAL_LIMIT);
  });

  protected readonly overflow = computed(() => Math.max(0, this.items().length - INITIAL_LIMIT));

  public readonly args = input.required<PresentListArgs>();

  protected itemKey(item: ListItem): string {
    return `${item.kind}:${item.data.id}`;
  }

  protected toggleExpanded(): void {
    this.expanded.update(v => !v);
  }
}
